import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AvailableSubFlowItem, TestCase } from '@core/interfaces/contract';
import {
  EntityVersion,
  HubEntitiesSortType,
  HubEntityType,
} from '@core/interfaces/hub-entity-type';
import { HubHttpPaginationResponse } from '@core/interfaces/HubResponse';
import { Pagination } from '@core/interfaces/pagination';
import {
  DeleteBulkResponse,
  DeployedProjectResponse,
  HubProject,
  OverrideDraftOptions,
  OverrideDraftResponse,
  ProjectVersionLicense,
  PullProjectResponse,
} from '@core/interfaces/project';
import { ProjectTechnology } from '@core/interfaces/project-technology';
import { HubRuntimeEnvironmentType } from '@core/interfaces/runtime';
import { CreateBySolutionResponse } from '@core/interfaces/solution';
import { HubHttpService } from '@core/services/hub-http/hub-http.service';
import { PermissionsService } from '@core/services/permissions/permissions.service';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ConfigurationVariable } from '../../../features/hub/projects/project/overview-project/configuration/service/configuration';
import { BulkOutdatedMethodsDetectionResponse } from '@core/interfaces/asset';
import { BulkNamesResponse } from '@core/interfaces/common';
import {
  CreateApiFromGlobalModelRequest,
  CreateApiFromGlobalModelResponse,
} from '@core/interfaces/global-model';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService extends HubHttpService {
  override serviceUrl = `${this.backendUrl}projects`;

  private readonly permissionsService = inject(PermissionsService);
  private readonly availableSubFlowsCache = new Map<string, Observable<AvailableSubFlowItem[]>>();

  /**
   * Read bulk of projects
   * we wish to skip before selecting additional records as part of our pagination.
   * @param query consist of
   * sort - sorting option
   * withContracts - If true, returns only
   * the projects that contains contract, otherwise - all projects
   * pagination consist of
   * limit - the number of project the user want to get on each page
   * offset - the number of records
   */
  all(query?: {
    sort?: string;
    labels?: string[];
    withContracts?: boolean;
    pagination?: Pagination;
    draftVersionsWithContractOnly?: boolean;
    moduleTypes?: string;
    excludeModuleTypes?: string;
    search?: string;
  }): Observable<HubHttpPaginationResponse<HubProject>> {
    let params = new HttpParams({
      fromObject: {
        sort: `${query?.sort || HubEntitiesSortType.MODIFIED_DESC}`,
        with_contracts: `${!!query?.withContracts}`,
        draft_versions_with_contract_only: `${!!query?.draftVersionsWithContractOnly}`,
        labels: query?.labels?.filter((label) => label !== 'none').toString() || '',
        noneLabels: query?.labels?.some((label) => label === 'none') || false,
      },
    });

    if (query?.pagination?.limit !== undefined && query?.pagination?.limit !== null) {
      params = params.set('limit', query.pagination.limit.toString());
    }

    if (query?.pagination?.offset !== undefined && query?.pagination?.offset !== null) {
      params = params.set('offset', query.pagination.offset.toString());
    }

    if (query?.moduleTypes) {
      params = params.set('module_types', query.moduleTypes);
    }

    if (query?.excludeModuleTypes) {
      params = params.set('exclude_module_types', query.excludeModuleTypes);
    }

    if (query?.search) {
      params = params.set('search', query.search);
    }

    return this.get<HubHttpPaginationResponse<HubProject>>('', { params });
  }

  /** Get all project names for validation (e.g. duplicate check before create). */
  names(): Observable<BulkNamesResponse> {
    return this.get<BulkNamesResponse>('/names');
  }

  /**
   * Get project by id
   * @param id - the id of the project the user want to retrieve
   * @param version - the version of the project the user want to retrieve
   */
  show(id: string, version = 0): Observable<HubProject> {
    let params = new HttpParams();
    params = params.set('version', version);
    return this.get<HubProject>(`/${id}`, { params });
  }

  // Do Not Remove!!! This function is for demo purposes only and should not be used in production. Provided for Roi's demo on the 'modernization' tool
  showByName(name: string, version = 0): Observable<HubProject> {
    let params = new HttpParams();
    params = params.set('version', version);
    return this.get<HubProject>(`/${name}/read-by-name`, { params });
  }

  /**
   * Tag project by id
   * @param id - the id of the project the user wants to tag
   * @param reason - the reason for tagging a new version
   */
  tag(id: string, reason: string): Observable<EntityVersion> {
    return this.post<EntityVersion, { reason: string }>(`/${id}/versions`, { reason });
  }

  /**
   * Replaces the project draft with the contents of a tag version.
   * @param id - the id of the project
   * @param version - the tag version whose contents become the new draft
   * @param options - whether to tag the current draft first, and whether to publish its methods
   * @returns the source version, the backup tag created from the discarded draft, and how many
   * methods were published
   */
  overrideDraftFromVersion(
    id: string,
    version: number,
    options: OverrideDraftOptions,
  ): Observable<OverrideDraftResponse> {
    return this.post<OverrideDraftResponse, OverrideDraftOptions>(
      `/${id}/versions/${version}/override-draft`,
      options,
    );
  }

  create(
    project: { name: string; description?: string; technology?: ProjectTechnology },
    modulesIds?: string[],
    isTransformed?: boolean,
  ): Observable<HubProject> {
    const projectData = {
      name: project.name,
      description: project.description ?? '',
      technology: project.technology,
    };

    const projectDataWithConfig = isTransformed
      ? { ...projectData, configuration: { isTransformed: true } }
      : projectData;

    const bodyPayload =
      modulesIds && modulesIds?.length > 0
        ? {
            project: projectDataWithConfig,
            modulesIds,
          }
        : projectDataWithConfig;

    return this.post<
      HubProject,
      | {
          project: {
            name: string;
            description: string;
            technology: ProjectTechnology | undefined;
            configuration?: { isTransformed: boolean };
          };
          modulesIds: string[];
        }
      | {
          name: string;
          description: string;
          technology: ProjectTechnology | undefined;
          configuration?: { isTransformed: boolean };
        }
    >('', bodyPayload).pipe(
      tap(() => this.permissionsService.invalidateCurrentUserResourceRoles()),
    );
  }

  update(
    id: string,
    data: { name: string; description: string; technology?: ProjectTechnology },
  ): Observable<void> {
    return this.put<
      void,
      {
        name: string;
        description: string;
        technology?: ProjectTechnology | undefined;
      }
    >(`/${id}`, data);
  }

  /**
   * Remove a project
   * @param id - the id of the given project
   */
  destroy(id: string): Observable<void> {
    return this.delete<void>(`/${id}`);
  }

  destroyBulk(ids: string[]): Observable<DeleteBulkResponse> {
    return this.delete<DeleteBulkResponse>('/bulk/delete', { body: { ids } });
  }

  addModulesToOne(projectId: string, modulesIds: string[]): Observable<void> {
    return this.put<void, { modulesIds: string[] }>(`/${projectId}/modules`, { modulesIds });
  }

  // this endpoint should be removed after backend refactor.
  additionalInfo(
    id: string,
    hubEntity: HubEntityType.Asset | HubEntityType.Module | HubEntityType.Method,
  ): Observable<{
    elements: {
      id: string;
      name: string;
      moduleId?: string;
      contractId?: string;
      versionCount?: number;
    }[];
  }> {
    const typeFlyout = `${hubEntity.toLowerCase()}s`;

    return this.get<{
      elements: {
        id: string;
        name: string;
        moduleId?: string;
        contractId?: string;
        versionCount?: number;
      }[];
    }>(`/${id}/flyout`, {
      params: {
        type: typeFlyout,
      },
    });
  }

  detachModule(projectId: string, moduleId: string): Observable<void> {
    return this.delete<void>(`/${projectId}/modules/${moduleId}`);
  }

  createBySolution(
    solutionName: string,
    projectName?: string,
    contractId?: string,
  ): Observable<CreateBySolutionResponse> {
    return this.post<
      CreateBySolutionResponse,
      {
        solutionName;
        projectName;
        contractId;
      }
    >(`/create-by-solution`, {
      solutionName,
      projectName,
      contractId,
    }).pipe(tap(() => this.permissionsService.invalidateCurrentUserResourceRoles()));
  }

  deploy(
    projectId: string,
    version: number,
    activeProfiles: string[],
    configurationVariables: ConfigurationVariable[],
    applicationName: string,
    environmentType?: HubRuntimeEnvironmentType,
    includeAllAssets?: boolean,
    deployer?: string,
    profile?: string,
  ): Observable<DeployedProjectResponse> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('version', version);

    if (includeAllAssets !== null && includeAllAssets !== undefined) {
      queryParams = queryParams.set('includeAllAssets', `${includeAllAssets}`);
    }
    if (deployer) {
      queryParams = queryParams.set('deployer', deployer);
    }

    if (profile) {
      queryParams = queryParams.set('profile', profile);
    }

    return this.post<
      DeployedProjectResponse,
      {
        environmentType?: HubRuntimeEnvironmentType;
        activeProfiles: string[];
        configurationVariables: ConfigurationVariable[];
        applicationName: string;
      }
    >(
      `/${projectId}/deploy`,
      { environmentType, activeProfiles, configurationVariables, applicationName },
      { params: queryParams },
    );
  }

  getDeployedJobStatus(
    projectId: string,
    deploymentId: string,
    deployer?: string,
  ): Observable<DeployedProjectResponse> {
    let queryParams = new HttpParams();
    if (deployer) {
      queryParams = queryParams.set('deployer', deployer);
    }
    return this.get<DeployedProjectResponse>(`/${projectId}/deployments/${deploymentId}`, {
      params: queryParams,
    });
  }

  duplicate(
    project: {
      id: string;
      duplicatedProjectName: string;
      version: number;
      duplicatedProjectDescription?: string;
    },
    publish: boolean,
    includeAttachments = true,
  ): Observable<{ id: string }> {
    const queryParam = new HttpParams({
      fromObject: {
        includeAttachments,
        publish,
      },
    });

    return this.post<
      { id: string },
      { projectName: string; version: number; description: string | undefined }
    >(
      `/${project.id}/duplicate`,
      {
        projectName: project.duplicatedProjectName,
        version: project.version,
        description: project?.duplicatedProjectDescription,
      },
      { params: queryParam },
    );
  }

  /**
   * Pull project information by name
   * @param projectName string project name
   * @param projectVersion number project version, default 0
   * @param excludeAssetsInUse boolean exclude assets used in contract flow, default undefined
   */
  pullByName(
    projectName: string,
    projectVersion = 0,
    excludeAssetsInUse?: boolean,
  ): Observable<PullProjectResponse> {
    const params: Record<string, string | number | boolean> = {
      version: projectVersion,
    };
    if (excludeAssetsInUse !== undefined) {
      params.excludeAssetsInUse = excludeAssetsInUse;
    }
    const queryParams = new HttpParams({
      fromObject: params,
    });
    return this.get<PullProjectResponse>(`/${projectName}/pull-by-name`, {
      params: queryParams,
    });
  }

  getOpenApiSpec(id: string, version = 0, openApiVersion?: string) {
    let url = `/${id}/openapi?version=${version}`;
    if (openApiVersion) {
      url += `&openApiVersion=${encodeURIComponent(openApiVersion)}`;
    }
    return this.get(url);
  }

  getLicense(projectId: string, tag: number, source: string): Observable<ProjectVersionLicense> {
    return this.get<ProjectVersionLicense>(`/${projectId}/license?version=${tag}&source=${source}`);
  }

  exportOPZ(
    id: string,
    name: string,
    version: number,
    includeAllAssets: boolean,
    includeLicense: boolean,
    includeConnectionProps: boolean,
    compatibilityVersion?: string,
  ): Observable<{ presignedUrl: string }> {
    const queryParam: Record<string, boolean | string | number> = {
      version,
      includeAllAssets,
      includeLicense,
      includeConnectionProps,
    };

    if (compatibilityVersion) {
      queryParam['compatibilityVersion'] = compatibilityVersion;
    }

    return this.post<
      { presignedUrl: string },
      {
        id: string;
        name: string;
      }
    >(
      `/opz`,
      {
        id,
        name,
      },
      { params: queryParam },
    );
  }

  flowTestCases(projectId: string, version: number): Observable<{ flowTestCases: TestCase[] }> {
    return this.get<{ flowTestCases: TestCase[] }>(
      `/${projectId}/flow-test-cases?version=${version}`,
    );
  }

  /**
   * Bulk detect outdated methods across multiple projects
   * Finds methods that use outdated asset versions in multiple projects
   * @param projectIds - Array of project IDs
   * @param projectVersion - Optional project version (defaults to 0 for draft)
   */
  bulkDetectOutdatedMethods(
    projectIds: string[],
    projectVersion: number = 0,
  ): Observable<BulkOutdatedMethodsDetectionResponse> {
    if (!projectIds || projectIds.length === 0) {
      throw new Error('projectIds array cannot be empty');
    }

    return this.post<
      BulkOutdatedMethodsDetectionResponse,
      { projectIds: string[]; projectVersion: number }
    >(`/bulk-outdated-methods`, { projectIds, projectVersion });
  }

  createApiFromGlobalModel(
    request: CreateApiFromGlobalModelRequest,
  ): Observable<CreateApiFromGlobalModelResponse> {
    return this.post<CreateApiFromGlobalModelResponse, CreateApiFromGlobalModelRequest>(
      '/create-api-from-global-model',
      request,
    );
  }

  getAvailableSubFlows(
    projectId: string,
    flowId: string,
    version?: number,
  ): Observable<AvailableSubFlowItem[]> {
    const cacheKey = `${projectId}:${flowId}:${version ?? ''}`;
    const cached = this.availableSubFlowsCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const params =
      version !== undefined
        ? new HttpParams().set('flowId', flowId).set('version', version)
        : new HttpParams().set('flowId', flowId);
    const request$ = this.get<{ flows: AvailableSubFlowItem[] }>(
      `/${projectId}/available-sub-flows`,
      { params },
    ).pipe(
      map(({ flows }) => flows),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.availableSubFlowsCache.set(cacheKey, request$);
    return request$;
  }

  invalidateAvailableSubFlows(): void {
    this.availableSubFlowsCache.clear();
  }
}
